/**
 * For You Widget - Two Column Layout
 * 
 * Shows one kink at a time with:
 * - LEFT: Rating bars for all 3 perspectives (Dom, Sub, Fantasy)
 * - RIGHT: Kink description with Read More button
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ChevronRight, Loader2, RefreshCw, Crown, Heart as HeartIcon, Eye, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RatingWidget from './RatingWidget';
import { API_BASE } from '../utils/api';
import { filterVisibleKinks } from '../utils/kinkVisibility.js';

// Perspective configurations
const PERSPECTIVES = [
    { key: 'dom', label: 'Dom', icon: Crown, color: 'text-amber-400' },
    { key: 'sub', label: 'Sub', icon: HeartIcon, color: 'text-pink-400' },
    { key: 'fantasy_only', label: 'Fantasy', icon: Eye, color: 'text-purple-400' }
];

function ForYouWidget() {
    const { activePersona, getAuthHeaders } = useAuth();

    const [recommendations, setRecommendations] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchRecommendations = async () => {
        if (!activePersona) {
            setRecommendations([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        setCurrentIndex(0);

        // Helper to fetch random kinks as fallback
        const fetchRandomKinks = async () => {
            const randomPage = Math.floor(Math.random() * 50) + 1;
            const response = await fetch(`${API_BASE}/api/kinks?limit=10&page=${randomPage}`);
            if (response.ok) {
                const data = await response.json();
                return filterVisibleKinks(data.kinks || []).map(k => ({
                    ...k,
                    type: 'kink',
                    alignment: 'wildcard'
                }));
            }
            return [];
        };

        try {
            // Try the recommendations endpoint first
            const headers = await getAuthHeaders();
            const response = await fetch(
                `${API_BASE}/api/ratings/${activePersona.id}/recommendations/mix`,
                { headers }
            );

            let recs = [];
            if (response.ok) {
                const data = await response.json();
                recs = filterVisibleKinks((data.recommendations || []).filter(item => item.type === 'kink')).concat(
                    (data.recommendations || []).filter(item => item.type !== 'kink')
                );
            }

            // If no recommendations, fetch random kinks
            if (recs.length === 0) {
                recs = await fetchRandomKinks();
            }

            setRecommendations(recs);
        } catch (err) {
            console.error('Error fetching recommendations:', err);
            // Always fallback to random kinks on error
            try {
                const recs = await fetchRandomKinks();
                setRecommendations(recs);
            } catch {
                setError('Could not load recommendations');
                setRecommendations([]);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecommendations();
    }, [activePersona]);

    const handleNext = () => {
        if (currentIndex < recommendations.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            fetchRecommendations();
        }
    };

    const currentItem = recommendations[currentIndex];

    if (!activePersona) return null;

    if (loading) {
        return (
            <div className="glass-card p-4 border border-wine-700/30">
                <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-wine-400" />
                    <span className="font-display font-bold text-cream text-sm">For You</span>
                </div>
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-wine-400" />
                </div>
            </div>
        );
    }

    if (!currentItem) {
        return (
            <div className="glass-card p-4 border border-wine-700/30">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-wine-400" />
                        <span className="font-display font-bold text-cream text-sm">For You</span>
                    </div>
                    <button onClick={fetchRecommendations} className="p-1.5 text-dark-400 hover:text-cream">
                        <RefreshCw size={14} />
                    </button>
                </div>
                <p className="text-dark-400 text-xs text-center py-4">No recommendations. Try again later.</p>
            </div>
        );
    }

    // Get description text
    const description = currentItem.description || currentItem.summary || 'No description available.';

    return (
        <div className="glass-card border border-wine-700/30">
            {/* Header */}
            <div className="px-4 py-2 border-b border-wine-700/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-wine-400" />
                    <span className="font-display font-bold text-cream text-sm">For You</span>
                    <span className="text-xs text-dark-500">({currentIndex + 1}/{recommendations.length})</span>
                </div>
                <button onClick={fetchRecommendations} className="p-1.5 text-dark-400 hover:text-cream" title="Refresh">
                    <RefreshCw size={14} />
                </button>
            </div>

            {/* Two-Column Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentItem.id}
                    initial={{ opacity: 0, x: 15 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -15 }}
                    transition={{ duration: 0.15 }}
                    className="p-4"
                >
                    {/* Kink Title */}
                    <div className="mb-3">
                        <Link
                            to={`/kink/${currentItem.id}`}
                            className="text-lg font-display font-bold text-cream hover:text-wine-300"
                        >
                            {currentItem.name}
                        </Link>
                        {currentItem.category && (
                            <span className="ml-2 text-xs text-wine-400 bg-wine-900/30 px-2 py-0.5 rounded">
                                {currentItem.category}
                            </span>
                        )}
                    </div>

                    {/* Two Columns: Ratings | Description */}
                    <div className="flex gap-4">
                        {/* LEFT: Rating Controls */}
                        <div className="flex-1 min-w-0 space-y-2">
                            {PERSPECTIVES.map((p) => {
                                const Icon = p.icon;
                                return (
                                    <div key={p.key} className="flex items-center gap-2">
                                        <div className="flex items-center gap-1 w-16 flex-shrink-0">
                                            <Icon className={`w-3 h-3 ${p.color}`} />
                                            <span className={`text-xs ${p.color}`}>{p.label}</span>
                                        </div>
                                        <div className="flex-1">
                                            <RatingWidget
                                                entityType={currentItem.type || 'kink'}
                                                entityId={currentItem.id}
                                                perspective={p.key}
                                                variant="buttons"
                                                size="sm"
                                                showLabels={true}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* RIGHT: Description */}
                        <div className="w-48 flex-shrink-0 flex flex-col">
                            <p className="text-xs text-cream/70 leading-relaxed line-clamp-4 flex-1">
                                {description}
                            </p>
                            <Link
                                to={`/kink/${currentItem.id}`}
                                className="mt-2 text-xs text-wine-400 hover:text-wine-300 flex items-center gap-1"
                            >
                                Read More <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Next Button */}
            <div className="px-4 py-2 border-t border-wine-700/20">
                <button
                    onClick={handleNext}
                    className="btn-ghost w-full py-2 text-sm flex items-center justify-center gap-1"
                >
                    {currentIndex < recommendations.length - 1 ? (
                        <>Next <ChevronRight className="w-4 h-4" /></>
                    ) : (
                        <>More <RefreshCw className="w-3 h-3" /></>
                    )}
                </button>
            </div>
        </div>
    );
}

export default ForYouWidget;
